import {
	type ProethicaModuleKind,
	proethicaModuleListMeta,
} from "@constants/demo/proethica-modules";
import {
	type ProethicaTestDescriptor,
	type ProethicaTestKind,
	getProethicaModuleNumber,
	getProethicaTestByCollectionId,
	getProethicaTestByKind,
	proethicaTestDescriptors,
} from "@constants/demo/proethica-tests";
import type { ModuleSession } from "@features/session/hooks/useProethicaSessionStore";
import type { Distribution, MixedItemTag } from "@memorang/types";
import type { TFunction } from "i18next";
import type { EXAM_STATUS } from "types/tag";
import { buttonConfig, statusMeta } from "../constants";

// Staged unlock marks entries non-interactive via `disabled`.
// Premium gating uses `hasAccess` (routes to the purchase flow).
export type ProethicaListTag = MixedItemTag & { disabled?: boolean };

// The roster is sourced from the live `exams` array (the only source carrying
// every module incl. content-only Module 1 and the native pre/post tests, each
// with a collectionId, title and status). Identity is the collectionId.
export type ProethicaExam = {
	collectionId?: number;
	title?: string;
	status?: EXAM_STATUS;
	numItems?: number;
	numericId?: number | null;
	distribution?: Distribution;
};

const EMPTY_DIST: Distribution = { high: 0, low: 0, medium: 0, none: 0 };

const examModuleNumber = (exam: ProethicaExam): number =>
	Number(exam.title?.match(/Module\s+(\d+)/i)?.[1] ?? Number.MAX_SAFE_INTEGER);

const isModuleExam = (exam: ProethicaExam): boolean =>
	/^Module\s+\d+/i.test(exam.title ?? "");

const moduleExamToTag = (exam: ProethicaExam): MixedItemTag =>
	({
		id: String(exam.collectionId),
		title: exam.title,
		label: exam.title,
		type: "Learning Module",
		displayType: ["Display", "Filter", "Progress"],
		distribution: exam.distribution ?? EMPTY_DIST,
		numItems: exam.numItems ?? 0,
		locked: false,
	}) as MixedItemTag;

const testExamToTag = (
	exam: ProethicaExam,
	descriptor: ProethicaTestDescriptor,
): MixedItemTag =>
	({
		id: String(exam.collectionId),
		title: descriptor.title,
		label: descriptor.title,
		type: "Practice Test",
		displayType: ["Display"],
		distribution: exam.distribution ?? {
			...EMPTY_DIST,
			none: exam.numItems ?? 0,
		},
		numItems: exam.numItems ?? 0,
		locked: false,
	}) as MixedItemTag;

export const getModuleLabel = (tag: MixedItemTag) =>
	tag.label || tag.title || "";

export const getModuleMetaKey = (tag: MixedItemTag) => {
	const label = getModuleLabel(tag);
	const match = label.match(/Module\s+\d+/i);
	return match ? match[0] : label;
};

export const getProethicaModuleKind = (
	tag: MixedItemTag,
): ProethicaModuleKind => {
	const meta = proethicaModuleListMeta[getModuleMetaKey(tag)];
	return meta?.moduleKind ?? "standard";
};

// Test rows excluded; use getProethicaOrderedEntries for the full ordered roster.
export const getProethicaModuleTags = (
	exams: ProethicaExam[] = [],
): MixedItemTag[] =>
	exams
		.filter(isModuleExam)
		.sort((a, b) => examModuleNumber(a) - examModuleNumber(b))
		.map(moduleExamToTag);

// The dashboard learning path: the 8 modules with each pre/post test inserted
// after its anchor module. Advancing through this list (vs. module-number+1)
// keeps the report's "continue" in lockstep with the gating unlock model.
export const getProethicaOrderedEntries = (
	exams: ProethicaExam[] = [],
): MixedItemTag[] => {
	const examByCollectionId = new Map(
		exams.map((exam) => [exam.collectionId, exam]),
	);
	const result: MixedItemTag[] = [];
	for (const exam of exams
		.filter(isModuleExam)
		.sort((a, b) => examModuleNumber(a) - examModuleNumber(b))) {
		result.push(moduleExamToTag(exam));
		const descriptor = proethicaTestDescriptors.find(
			(test) => test.anchorModuleNumber === examModuleNumber(exam),
		);
		const testExam = descriptor
			? examByCollectionId.get(descriptor.collectionId)
			: undefined;
		if (descriptor && testExam) {
			result.push(testExamToTag(testExam, descriptor));
		}
	}
	return result;
};

export const applyProethicaPremiumLock = (
	items: MixedItemTag[],
	_hasAccess: boolean,
	_freeTagIds: string[] = [],
): MixedItemTag[] => items;

export const getProethicaEntryShortLabel = (tag: MixedItemTag): string =>
	getProethicaTestByCollectionId(tag.id)?.title ?? getModuleMetaKey(tag);

const deriveStatus = (tag: MixedItemTag): EXAM_STATUS => {
	if ((tag.averageScore ?? 0) >= 85 && (tag.distribution?.none ?? 0) === 0) {
		return "COMPLETE";
	}
	if ((tag.averageScore ?? 0) > 0) {
		return "ACTIVE";
	}
	return "NOT_STARTED";
};

export type ProethicaLiveStatusSignals = {
	// Live per-collection status from `exams`. Authoritative for scored modules.
	examStatusByCollectionId: Record<string, EXAM_STATUS | undefined>;
	preTestCompleted: boolean;
	postTestPassed: boolean;
};

export type ProethicaTestCompletionSignals = {
	preTestCompleted: boolean;
	postTestPassed: boolean;
};

// A failed-but-submitted posttest still reads COMPLETE via the backend exam
// status; the postTestPassed flag alone would report it incomplete.
export const isProethicaTestComplete = (
	kind: ProethicaTestKind,
	examStatus: EXAM_STATUS | undefined,
	{ preTestCompleted, postTestPassed }: ProethicaTestCompletionSignals,
): boolean => {
	const flagComplete = kind === "pretest" ? preTestCompleted : postTestPassed;
	return flagComplete || examStatus === "COMPLETE";
};

export const resolveProethicaSessionNumericId = (
	exam: ProethicaExam | undefined,
	tracked: ModuleSession | undefined,
): number | undefined => {
	const examNumericId = exam?.numericId;
	if (examNumericId != null && examNumericId > 0) {
		return examNumericId;
	}
	const trackedNumericId = tracked?.numericId;
	return trackedNumericId != null && trackedNumericId > 0
		? trackedNumericId
		: undefined;
};

export type ProethicaCompletedEntryAction =
	| { kind: "report"; numericId: number }
	| { kind: "blocked" }
	| { kind: "start" };

// Backend-driven press dispatch for a module/test row: a completed entry
// routes to its report (the exam row's numericId works on any device; the
// locally tracked session is only a fallback for the window before the
// distribution refetch). An unresolvable completed entry is blocked (fail
// closed) rather than silently minting a new session, except the post-test,
// whose restart is a legitimate retake.
export const resolveProethicaCompletedEntryAction = (
	moduleId: string,
	exam: ProethicaExam | undefined,
	tracked: ModuleSession | undefined,
	signals: ProethicaTestCompletionSignals,
): ProethicaCompletedEntryAction => {
	const test = getProethicaTestByCollectionId(moduleId);
	const complete = test
		? isProethicaTestComplete(test.kind, exam?.status, signals)
		: exam?.status === "COMPLETE";
	if (!complete) {
		return { kind: "start" };
	}
	const numericId = resolveProethicaSessionNumericId(exam, tracked);
	if (numericId != null) {
		return { kind: "report", numericId };
	}
	return test?.kind === "posttest" ? { kind: "start" } : { kind: "blocked" };
};

export const resolveProethicaLiveStatus = (
	tag: MixedItemTag,
	{
		examStatusByCollectionId,
		preTestCompleted,
		postTestPassed,
	}: ProethicaLiveStatusSignals,
): EXAM_STATUS | undefined => {
	const id = String(tag.id);
	const examStatus = examStatusByCollectionId[id];
	const test = getProethicaTestByCollectionId(id);
	if (test) {
		const override = isProethicaTestComplete(test.kind, examStatus, {
			preTestCompleted,
			postTestPassed,
		});
		return override ? "COMPLETE" : examStatus;
	}
	if (examStatus === "COMPLETE") {
		return "COMPLETE";
	}
	return examStatus;
};

// The scan wraps: modules 2-7 can be completed in any order, so the only entries
// left once the current one ends may sit earlier in the path.
export const selectProethicaNextAvailableEntry = (
	entries: ProethicaListTag[],
	currentModuleId: string | undefined,
	isComplete: (entry: MixedItemTag) => boolean,
): MixedItemTag | undefined => {
	const currentIndex = entries.findIndex(
		(entry) => String(entry.id) === currentModuleId,
	);
	const start = currentIndex >= 0 ? currentIndex + 1 : 0;
	const scanLength = currentIndex >= 0 ? entries.length - 1 : entries.length;

	for (let offset = 0; offset < scanLength; offset++) {
		const entry = entries[(start + offset) % entries.length];
		if (!(entry.disabled || isComplete(entry))) {
			return entry;
		}
	}
	return undefined;
};

const resolveCta = (status: EXAM_STATUS, t: TFunction, hasAccess = true) =>
	status === "COMPLETE"
		? t("dashboard.diagnostics.viewResults")
		: hasAccess
			? (buttonConfig[status].text ?? "Start")
			: "Unlock";

const getProethicaTestListProps = (
	test: ProethicaTestDescriptor,
	tag: MixedItemTag,
	t: TFunction,
	liveStatus?: EXAM_STATUS,
	hasAccess = true,
) => {
	const status = liveStatus ?? "NOT_STARTED";
	return {
		title: test.title,
		moduleTitle: test.title,
		moduleNumber: undefined,
		moduleKind: "standard" as ProethicaModuleKind,
		subtitle: test.subtitle,
		statusLabel: statusMeta[status].text,
		status,
		cta: resolveCta(status, t, hasAccess),
		thumbnailUrl: "",
		thumbnailSource: undefined,
		hasAccess,
		disabled: (tag as ProethicaListTag).disabled ?? false,
		id: String(tag.id),
		typeLabel: test.typeLabel,
		numItems: tag.numItems ?? 0,
		duration: undefined,
		durationLabel: undefined,
		isTest: true,
		testKind: test.kind as ProethicaTestKind | undefined,
		icon: test.icon as string | undefined,
	};
};

export const getProethicaModuleListProps = (
	tag: MixedItemTag,
	t: TFunction,
	liveStatus?: EXAM_STATUS,
	hasAccess = true,
) => {
	const test = getProethicaTestByCollectionId(tag.id);
	if (test) {
		return getProethicaTestListProps(test, tag, t, liveStatus, hasAccess);
	}

	const label = getModuleLabel(tag);
	const meta = proethicaModuleListMeta[getModuleMetaKey(tag)];
	const status = liveStatus ?? deriveStatus(tag);

	const typeLabel = meta?.moduleKindLabel ?? "Lesson";
	const duration =
		meta?.durationMinutes ??
		Math.max(2, Math.round((tag.numItems ?? 10) * 0.8));
	const durationLabel = meta?.durationLabel ?? `${duration} min`;

	return {
		title: meta?.titleWithNumber ?? meta?.title ?? tag.title ?? label,
		moduleTitle: meta?.title ?? tag.title ?? label,
		moduleNumber: meta?.moduleNumber,
		moduleKind: meta?.moduleKind ?? "standard",
		subtitle: meta?.subtitle ?? `${tag.numItems ?? 0} items`,
		statusLabel: statusMeta[status].text,
		status,
		cta: resolveCta(status, t, hasAccess),
		thumbnailUrl: meta?.thumbnailUrl ?? "",
		thumbnailSource: meta?.thumbnailSource,
		hasAccess,
		disabled: (tag as ProethicaListTag).disabled ?? false,
		id: String(tag.id),
		typeLabel,
		numItems: tag.numItems ?? 10,
		duration,
		durationLabel,
		isTest: false,
		testKind: undefined as ProethicaTestKind | undefined,
		icon: undefined as string | undefined,
	};
};

export type ProethicaGatingSignals = {
	preTestCompleted?: boolean;
	completedModuleNumbers?: number[];
};

const FIRST_MODULE_NUMBER =
	getProethicaTestByKind("pretest")?.anchorModuleNumber ?? 1;
const FINAL_MODULE_NUMBER =
	getProethicaTestByKind("posttest")?.anchorModuleNumber ?? 8;
const MIDDLE_MODULE_NUMBERS = Array.from(
	{ length: Math.max(0, FINAL_MODULE_NUMBER - FIRST_MODULE_NUMBER - 1) },
	(_, i) => FIRST_MODULE_NUMBER + 1 + i,
);

// Staged unlock: Module 1 -> Pre-Test -> Modules 2-7 -> final module -> Post-Test.
// preTestDone chains firstModuleComplete so a stale Pre-Test signal can't unlock
// later modules while Module 1 is still untouched.
const isProethicaEntryUnlocked = (
	tag: MixedItemTag,
	signals: ProethicaGatingSignals,
): boolean => {
	const completed = signals.completedModuleNumbers ?? [];
	const firstModuleComplete = completed.includes(FIRST_MODULE_NUMBER);
	const preTestDone =
		(signals.preTestCompleted ?? false) && firstModuleComplete;
	const middleModulesComplete = MIDDLE_MODULE_NUMBERS.every((n) =>
		completed.includes(n),
	);
	const finalModuleComplete = completed.includes(FINAL_MODULE_NUMBER);

	const test = getProethicaTestByCollectionId(tag.id);
	if (test) {
		return test.kind === "pretest" ? firstModuleComplete : finalModuleComplete;
	}
	const moduleNumber = getProethicaModuleNumber(tag);
	if (moduleNumber == null || moduleNumber === FIRST_MODULE_NUMBER) {
		return true;
	}
	if (moduleNumber === FINAL_MODULE_NUMBER) {
		return preTestDone && middleModulesComplete;
	}
	return preTestDone;
};

// Applies the staged unlock across the ordered roster (home, modules tab,
// quick-study).
export const resolveProethicaModuleItems = (
	items: MixedItemTag[],
	signals: ProethicaGatingSignals = {},
): MixedItemTag[] => {
	return items.map((tag) =>
		isProethicaEntryUnlocked(tag, signals)
			? tag
			: ({ ...tag, disabled: true } as ProethicaListTag),
	);
};

const quickStudySectionOrder: EXAM_STATUS[] = [
	"ACTIVE",
	"NOT_STARTED",
	"COMPLETE",
];

export const getProethicaModulesGroupedByStatus = (
	tags: MixedItemTag[],
	t: TFunction,
	getLiveStatus?: (tag: MixedItemTag) => EXAM_STATUS | undefined,
	hasAccess = true,
) => {
	const modules = tags.map((tag) =>
		getProethicaModuleListProps(tag, t, getLiveStatus?.(tag), hasAccess),
	);

	return quickStudySectionOrder
		.map((status) => ({
			status,
			title: t(`dashboard.quickStudy.sections.${status}`),
			modules: modules.filter((module) => module.status === status),
		}))
		.filter((section) => section.modules.length > 0);
};

export const getProethicaModuleInteractionState = (
	hasAccess: boolean,
	disabled: boolean | undefined,
	isComplete: boolean,
) => ({
	needsPurchase: !(hasAccess || isComplete),
	isCurriculumLocked: hasAccess && Boolean(disabled) && !isComplete,
});
